import { z } from "zod";
/*
 * A path into a document's structured extraction, such as `total`, `lines[1].net` or
 * `lines[*].net`. A review finding names the paths it rests on, so the review can mark those
 * values on the document; a reviewer's correction names the one value it replaces. Paths follow
 * the extraction schema: object properties by name, array items by index, `[*]` for every item.
 */
const SEGMENT = String.raw `[A-Za-z_][A-Za-z0-9_]*(?:\[(?:\d{1,4}|\*)\])?`;
const PATH = new RegExp(`^${SEGMENT}(?:\\.${SEGMENT})*$`, "u");
export const workflowDocumentPathSchema = z.string().max(200).regex(PATH);
/** A correction replaces one value, so it names exact indices. */
export const workflowCorrectionPathSchema = workflowDocumentPathSchema.refine((path) => !path.includes("[*]"), "A correction names one value; use an index, not [*]");
/**
 * A reviewer's corrections. `document` corrects what was read from the document, one value per
 * path; `fields` gives the reviewer's own answer for a proposed field, keyed by its field key.
 * They stay apart because an extraction key and a field key may share a name.
 */
export const workflowCorrectionsSchema = z.object({
    document: z.record(workflowCorrectionPathSchema, z.json()).optional(),
    fields: z.record(z.string().regex(/^[A-Za-z_][A-Za-z0-9_]{0,63}$/u), z.json()).optional(),
}).strict().refine((corrections) => Object.keys(corrections.document ?? {}).length + Object.keys(corrections.fields ?? {}).length <= 100, "At most 100 corrections");
export function workflowDocumentPathSteps(path) {
    return path.split(".").map((segment) => {
        const match = /^([A-Za-z_][A-Za-z0-9_]*)(?:\[(\d+|\*)\])?$/u.exec(segment);
        if (!match)
            throw new Error(`Invalid document path segment '${segment}'`);
        const index = match[2] === undefined ? null : match[2] === "*" ? "*" : Number(match[2]);
        return { key: match[1], index };
    });
}
const isRecord = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const allows = (schema, type) => schema.type === type || (Array.isArray(schema.type) && schema.type.includes(type));
/** Whether a declared extraction schema (JSON Schema) has the path: properties by name, array items by index. */
export function extractionSchemaHasPath(schema, path) {
    if (!workflowDocumentPathSchema.safeParse(path).success)
        return false;
    let node = schema;
    for (const step of workflowDocumentPathSteps(path)) {
        if (!isRecord(node) || !isRecord(node.properties) || !isRecord(node.properties[step.key]))
            return false;
        node = node.properties[step.key];
        if (step.index !== null) {
            if (!isRecord(node) || !allows(node, "array") || !isRecord(node.items))
                return false;
            node = node.items;
        }
    }
    return true;
}
